/**
 * Admin User List Page
 * Replaces the placeholder AdminConsolePage with functional user management interface
 * Uses TanStack Table for data display, search, filter, and pagination
 */

import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  type SortingState,
} from '@tanstack/react-table';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { UserAvatar } from '../../components/ui/UserAvatar';
import { UserEditModal } from '../../components/admin/UserEditModal';
import { UserDeletionModal } from '../../components/admin/UserDeletionModal';
import { useAdminUsersList } from '../../hooks/useAdminApi';
import { type UserProfile, type UserSearchResult } from '../../lib/types/user';
import { cn } from '../../lib/utils';

const columnHelper = createColumnHelper<UserProfile>();

type SearchType = 'email' | 'name';

export function UserListPage() {
  /**
   * `/admin?email=…` arrives from the subscriptions console, where a row knows
   * the provider's email but has no route to the account behind it. Seeded
   * once, as initial state rather than an effect, so the admin can then edit
   * the box normally without the URL fighting them.
   */
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get('email') ?? '';
  const [searchQuery, setSearchQuery] = useState(initialEmail);
  const [searchType, setSearchType] = useState<SearchType>('email');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [selectedUserForDeletion, setSelectedUserForDeletion] =
    useState<UserProfile | null>(null);
  const [validationError, setValidationError] = useState<string>('');

  // Fetch users with current filters
  const {
    data: usersData,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAdminUsersList({
    emailSearch:
      searchType === 'email' && searchQuery ? searchQuery : undefined,
    nameSearch: searchType === 'name' && searchQuery ? searchQuery : undefined,
    limit: 25,
  });

  // Memoize flattened user data to prevent recomputation on every render
  const users = useMemo(
    () =>
      usersData?.pages.flatMap((page: UserSearchResult) => page.users) || [],
    [usersData]
  );

  // Table columns definition
  const columns = useMemo(
    () => [
      columnHelper.accessor('display_name', {
        header: 'User',
        cell: ({ row, getValue }) => (
          <div className="flex items-center space-x-3">
            <UserAvatar
              displayName={getValue()}
              photoUrls={row.original.photo_url}
              size="sm"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {getValue()}
              </p>
              {row.original.email && (
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {row.original.email}
                </p>
              )}
              <p className="text-xs text-gray-400 dark:text-gray-500 truncate font-mono">
                {row.original.uid}
              </p>
            </div>
          </div>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('email_verified', {
        header: 'Email Status',
        cell: ({ getValue }) => (
          <span
            className={cn(
              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
              getValue()
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
            )}
          >
            {getValue() ? 'Verified' : 'Unverified'}
          </span>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('created_at', {
        header: 'Joined',
        cell: ({ getValue }) => {
          const value = getValue();
          const date = value instanceof Date ? value : new Date(value);
          return (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {date.toLocaleDateString()}
            </span>
          );
        },
        enableSorting: true,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setSelectedUser(row.original)}
              className="text-sky-600 hover:text-sky-900 dark:text-sky-400 dark:hover:text-sky-300 text-sm font-medium"
            >
              Edit
            </button>
            <button
              onClick={() => setSelectedUserForDeletion(row.original)}
              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium inline-flex items-center"
            >
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              Delete
            </button>
          </div>
        ),
      }),
    ],
    []
  );

  // Initialize table with flattened paginated data
  const table = useReactTable({
    data: users,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Handle search input
  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const query = (formData.get('search') as string).trim();

    // No validation needed - backend supports prefix matching for both email and name searches
    setValidationError('');
    setSearchQuery(query);
  };

  // Handle search type change
  const handleSearchTypeChange = (newType: SearchType) => {
    setSearchType(newType);
    setValidationError('');
    // Clear search query when changing type to avoid confusion
    setSearchQuery('');
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            User Management
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage user accounts and roles
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col gap-4">
              {/* Search Type Radio Buttons */}
              <div className="flex items-center space-x-6">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="searchType"
                    value="email"
                    checked={searchType === 'email'}
                    onChange={() => handleSearchTypeChange('email')}
                    className="text-sky-600 focus:ring-sky-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Search by email
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="searchType"
                    value="name"
                    checked={searchType === 'name'}
                    onChange={() => handleSearchTypeChange('name')}
                    className="text-sky-600 focus:ring-sky-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Search by name
                  </span>
                </label>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                {/* Search Form */}
                <form onSubmit={handleSearch} className="flex-1">
                  <div>
                    <div className="flex">
                      <input
                        type="text"
                        name="search"
                        placeholder={
                          searchType === 'email'
                            ? 'Search by email...'
                            : 'Search by display name...'
                        }
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="block w-full rounded-l-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                      />
                      <button
                        type="submit"
                        className="inline-flex items-center px-4 py-2 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-md bg-gray-50 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </button>
                    </div>
                    {validationError && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                        {validationError}
                      </p>
                    )}
                  </div>
                </form>

                {/* Clear Filters */}
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSearchType('email');
                      setValidationError('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Results Info */}
          <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {isLoading ? (
                  'Loading...'
                ) : error ? (
                  <span className="text-red-600 dark:text-red-400">
                    Error loading users
                  </span>
                ) : (
                  <>
                    Showing <span className="font-medium">{users.length}</span>{' '}
                    users loaded
                  </>
                )}
              </p>
              {error && (
                <button
                  onClick={() => refetch()}
                  className="text-sm font-medium text-sky-600 hover:text-sky-500 dark:text-sky-400 dark:hover:text-sky-300"
                >
                  Retry
                </button>
              )}
            </div>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="lg" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <p className="text-gray-500 dark:text-gray-400 mb-2">
                    Failed to load users
                  </p>
                  <button
                    onClick={() => refetch()}
                    className="text-sky-600 hover:text-sky-500 dark:text-sky-400 dark:hover:text-sky-300 font-medium"
                  >
                    Try again
                  </button>
                </div>
              </div>
            ) : !users.length ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <p className="text-gray-500 dark:text-gray-400">
                    {searchQuery
                      ? `No users found matching ${searchType}: "${searchQuery}"`
                      : 'No users found'}
                  </p>
                </div>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <div className="flex items-center space-x-1">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {header.column.getCanSort() && (
                              <span className="text-gray-400">
                                {{
                                  asc: '↑',
                                  desc: '↓',
                                }[header.column.getIsSorted() as string] ??
                                  '↕️'}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {table.getRowModel().rows.map(row => (
                    <tr
                      key={row.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {hasNextPage && (
            <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Showing first {users.length} results
                </p>
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-md border',
                    isFetchingNextPage
                      ? 'text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 cursor-not-allowed'
                      : 'text-sky-600 dark:text-sky-400 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                  )}
                >
                  {isFetchingNextPage ? (
                    <span className="flex items-center">
                      <LoadingSpinner size="sm" className="mr-2" />
                      Loading...
                    </span>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* User Edit Modal */}
      {selectedUser && (
        <UserEditModal
          user={selectedUser}
          isOpen={true}
          onClose={() => setSelectedUser(null)}
        />
      )}

      {/* User Deletion Modal */}
      {selectedUserForDeletion && (
        <UserDeletionModal
          user={selectedUserForDeletion}
          isOpen={true}
          onClose={() => setSelectedUserForDeletion(null)}
        />
      )}
    </AdminLayout>
  );
}
